import { useState } from "react";

import {
  formatNumberToShort,
  formatUSDShort,
  formatPercent,
  peopleOrPerson,
} from "@/web/lib/formatting";
import { BusinessCard, useDropdownLock } from "@/web/routes/browse/-components/cards/BusinessCard";
import {
  BrowseCardTimer,
  CardActions,
  CardTypeBadge,
  MetricCell,
} from "@/web/routes/browse/-components/cards/cardUtils";
import type { RoadshowBrowseResult } from "@/web/routes/browse/-components/types";

const LAUNCHED_BY_ENABLED = false;

export function RoadshowCard({ browseResult }: { browseResult: RoadshowBrowseResult }) {
  const [popoverOpen, setPopoverOpen] = useState(false);
  const { lock: lockDropdown, unlock: unlockDropdown } = useDropdownLock();

  const handleSetPopoverOpen = (open: boolean) => {
    setPopoverOpen(open);
    if (open) lockDropdown();
    else unlockDropdown();
  };

  const { campaign } = browseResult;
  const targetReturnPercent = campaign.finances.targetReturn * 100;

  const campaignColors = campaign.colors
    ? {
        primary: campaign.colors.primary,
        secondary: campaign.colors.secondary,
        tertiary: campaign.colors.tertiary,
        text: campaign.colors.text,
      }
    : undefined;

  return (
    <BusinessCard
      colors={campaignColors}
      bgImage={campaign.heroImageCdnPath}
      fullBleedBackground
      className="border-none"
    >
      <BusinessCard.TopBar>
        <div className="flex items-center gap-2">
          <CardTypeBadge type="roadshow" />
          <BrowseCardTimer endTimestamp={browseResult.endTimestamp} />
        </div>
        <BusinessCard.Actions>
          <CardActions
            campaignId={campaign.campaignId}
            popoverOpen={popoverOpen}
            setPopoverOpen={handleSetPopoverOpen}
          />
        </BusinessCard.Actions>
      </BusinessCard.TopBar>

      <BusinessCard.Body>
        {/* TODO: link to /campaigns/$campaignId once that route exists in Tycho */}
        <div className="flex flex-1 flex-col text-white">
          <BusinessCard.Title>{browseResult.title}</BusinessCard.Title>
          <BusinessCard.Tags>
            <BusinessCard.Tag>{browseResult.location}</BusinessCard.Tag>
            <BusinessCard.Tag>{browseResult.businessTypeText}</BusinessCard.Tag>
          </BusinessCard.Tags>
          {browseResult.launchedBy && LAUNCHED_BY_ENABLED ? (
            <div className="mt-4 flex flex-row items-center gap-1">
              <div className="text-[10px] tracking-widest text-white/50 uppercase">Launched By</div>
              <span className="text-[10px] text-white">{browseResult.launchedBy}</span>
            </div>
          ) : null}
        </div>
      </BusinessCard.Body>

      <BusinessCard.Metrics>
        <div className="flex items-stretch divide-x divide-white/15 text-white">
          <MetricCell
            label="Interested"
            value={formatNumberToShort(browseResult.totalInterested, { showThousands: true })}
            secondaryValue={peopleOrPerson(browseResult.totalInterested)}
            className="pr-4"
          />
          <MetricCell
            label="Interest"
            value={formatUSDShort(browseResult.totalInterest)}
            className="px-4"
          />
          <MetricCell
            label="Target Return"
            value={`${formatPercent(targetReturnPercent)}%*`}
            className="flex-1 pl-4"
          />
        </div>
      </BusinessCard.Metrics>
    </BusinessCard>
  );
}
